import lodashCapitalize from "lodash/capitalize";
import { Phone } from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import AudioElement from "components/AudioElement";
import CallTranscriptButton from "components/CallTranscript/CallTranscriptButton";
import withRoleCheck from "components/hocs/withRoleCheck";
import _EditCallModal from "components/modals/EditCallModal";
import EntryDetails from "components/LogItem/elements/EntryDetails";
import {
  basicDateFormatter,
  defaultDateTimeFormatter,
} from "components/LogItem/messages/dates";
import {
  CALL_SNAPSHOT_KEYS,
  renderInitialValues,
} from "components/LogItem/messages/initialValues";
import { getModelLink } from "components/LogItem/messages/modelLinks";

const EditCallModal = withRoleCheck(_EditCallModal, [
  ACL_RELATIONSHIPS.campaignTargetCall.edit,
]);

// Call-category actions that carry their own entry instead of being rendered
// as a call outcome.
export const CALL_ACTIONS_WITH_OWN_ENTRY = new Set([
  "call_status_verified",
  "call_status_unverified",
]);

const CALL_STATUSES = [
  "unknown",
  "failed_invalid",
  "failed_with_vm",
  "failed_no_vm",
  "failed_with_vm_email_optin",
  "failed_no_vm_email_optin",
  "live_invalid",
  "live_dnc",
  "live_unqualified",
  "live_callback",
  "live_qualified",
  "live_success",
  "call",
  "manual_call",
];

const isDispositionEdit = ({ previous_disposition, new_disposition }) =>
  !!(previous_disposition && new_disposition);

const isInboundCall = call_info =>
  call_info.call_direction?.toUpperCase() === "INBOUND";

function CallSummary({ item, contactLink }) {
  const { user_name, action, auditable_id, info, call_info } = item;
  const { other_comment, callback_time, is_manual, comments, date } = info;

  if (other_comment) {
    return (
      <>
        <p>
          {user_name} called {contactLink}.
        </p>
        <EntryDetails
          rows={[
            { label: "Status", value: "Other" },
            { label: "Comments", value: other_comment },
          ]}
        />
      </>
    );
  }

  const inbound = isInboundCall(call_info);
  return (
    <>
      {inbound ? (
        <p>
          {contactLink} called {user_name}.
        </p>
      ) : (
        <p>
          {user_name} called {contactLink}.
        </p>
      )}
      <EntryDetails
        rows={[
          { label: "Date", value: is_manual ? basicDateFormatter(date) : null },
          {
            label: "Number",
            value: inbound ? call_info.from : call_info.number_called,
          },
          {
            label: "Direction",
            value: lodashCapitalize(call_info.call_direction),
          },
          { label: "Status", value: action },
          { label: "Call ID", value: auditable_id },
          { label: "Comments", value: comments },
          {
            label: "Scheduled Callback",
            value: callback_time && defaultDateTimeFormatter(callback_time),
          },
        ]}
      />
    </>
  );
}

const CALL_ENTRY = {
  title: ({ info, call_info }) => {
    if (isDispositionEdit(info)) {
      return "Call Disposition Changed";
    }
    return isInboundCall(call_info) ? "Inbound Call" : "Outbound Call";
  },
  icon: Phone,
  message: item => {
    const { id, user_name, contact_id, contact_name, info, call_info } = item;
    const contactLink = getModelLink(contact_id, "Contact", {
      label: contact_name,
    });

    return (
      <>
        {isDispositionEdit(info) ? (
          <p>
            {user_name} changed the call disposition for {contactLink} from{" "}
            {info.previous_disposition} to {info.new_disposition}.
          </p>
        ) : (
          <CallSummary item={item} contactLink={contactLink} />
        )}
        {call_info.recording_url && !info.previous_disposition && (
          <div className="tw:mt-2">
            <AudioElement
              id={`audio-el-${id}`}
              src={call_info.recording_url}
              wide
            />
          </div>
        )}
        {renderInitialValues(info.initial_values, {
          keys: CALL_SNAPSHOT_KEYS,
        })}
      </>
    );
  },
  actions: ({
    dispositions,
    campaign_id,
    item: {
      action,
      call_info,
      info: { campaign_target_call_id, previous_disposition, transcript },
    },
    refreshLog,
  }) => (
    <>
      {!previous_disposition &&
        !!campaign_target_call_id &&
        action !== "delete" && (
          <EditCallModal
            iconTrigger
            callId={campaign_target_call_id}
            campaignId={campaign_id}
            callStatuses={dispositions}
            onSuccess={refreshLog}
          />
        )}
      <CallTranscriptButton
        callId={campaign_target_call_id}
        transcriptStatus={call_info?.transcript_status}
        transcriptSkipReason={call_info?.transcript_skip_reason}
        hasRecording={!!call_info?.recording_url}
        manualTranscript={transcript}
        onRequested={refreshLog}
      />
    </>
  ),
};

const verificationEntry = (title, state) => ({
  title,
  icon: Phone,
  message: ({ contact_name, contact_id, user_name }) => (
    <p>
      {user_name} marked the call with{" "}
      {getModelLink(contact_id, "Contact", { label: contact_name })} as {state}.
    </p>
  ),
});

export const CALL_EVENTS = {
  ...Object.fromEntries(CALL_STATUSES.map(status => [status, CALL_ENTRY])),
  call_status_verified: verificationEntry("Call Verified", "verified"),
  call_status_unverified: verificationEntry("Call Unverified", "unverified"),
};
